'use client';

/**
 * Click tracking for internal free-tool surfaces.
 *
 * Deliberately UTM-free. Internal links must not carry `utm_*` params: they
 * overwrite the visitor's real acquisition source in the attribution cookie, so
 * a user who arrived from organic search would look like they came from our own
 * tool page. First-party events carry the same information without that damage.
 *
 * Fires both sinks the codebase already uses together: `emitClientEvent` for
 * `event_log`/Mixpanel/GA4 server-side, and `pushToDataLayer` for GTM. Delivery
 * is best-effort and must never delay the navigation the user asked for.
 */

import { emitClientEvent } from '@/lib/analytics/emit-client-event';
import { pushToDataLayer } from '@/lib/analytics/gtm';

export type FreeToolCtaSurface =
  | 'cta_band'
  | 'related_tool'
  | 'hub_card'
  | 'nav_menu'
  | 'tool_page'
  /** The "Sign up free" button on a cap-rejection error panel (signup-gate test, 2026-09-16). */
  | 'cap_gate'
  /** The logged-in dashboard tools index page. */
  | 'dashboard_tools';

export interface FreeToolCtaClick {
  surface: FreeToolCtaSurface;
  /** Registry slug of the page the click happened ON, when there is one. */
  slug?: string;
  /** Where the click goes: a tool slug, or a path like /sign-up or /pricing. */
  destination: string;
  /** Button or link label, so a band's primary and secondary stay distinguishable. */
  label?: string;
}

export function trackFreeToolCtaClick({
  surface,
  slug,
  destination,
  label,
}: FreeToolCtaClick): void {
  emitClientEvent('/api/events/conversion', {
    name: 'free_tool_cta_clicked',
    surface,
    slug,
    destination,
    label,
  });
  pushToDataLayer({
    event: 'free_tool_cta_clicked',
    free_tool_cta_surface: surface,
    free_tool_cta_slug: slug,
    free_tool_cta_destination: destination,
    free_tool_cta_label: label,
  });
}
